/**
 * SmileO auto gift unit.
 *
 * When a customer has 4 paid units of a "bundle" variant (identified by the
 * `_bundle_free` line item property, set to "false" on normal paid lines by
 * the SmileO bundle add-to-cart flow), this automatically adds one more unit
 * of the same variant marked `_bundle_free: "true"`. Because it carries
 * different line item properties, Shopify creates it as its own cart line
 * (separate line key) rather than merging it into the paid line - so it
 * doesn't fight with whatever caps the paid line's own quantity at 4.
 *
 * The store's native "SmileO Gift 5th Unit" automatic discount (Buy 4, get 1
 * free) then makes that extra unit free once 5 total units of the variant
 * are present in the cart. The cart templates already render lines with
 * `final_line_price == 0` and a matching paid sibling as a "🎁 gift" row
 * (class `smio-cart-gift-tag`).
 *
 * If the paid quantity later drops back under 4 (item removed), the
 * auto-added gift line is removed again so the cart stays consistent.
 *
 * IMPORTANT: if the customer explicitly removes/declines the gift line
 * themselves (clicking its remove button, or its quantity minus button),
 * we remember that per variant (localStorage) and stop re-adding it - even
 * though paid quantity is still 4 - until the paid quantity drops below 4
 * and comes back up again (a fresh "reaching 4" moment). Without this, the
 * script would immediately re-add whatever the customer just removed,
 * making the remove button look broken.
 */
(function () {
  var GIFT_PROPERTY = '_bundle_free';
  var THRESHOLD = 4;
  var DECLINE_PREFIX = 'smio_gift_declined_';
  var busy = false;

  function isDeclined(variantId) {
    try {
      return window.localStorage.getItem(DECLINE_PREFIX + variantId) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDeclined(variantId, declined) {
    try {
      if (declined) {
        window.localStorage.setItem(DECLINE_PREFIX + variantId, '1');
      } else {
        window.localStorage.removeItem(DECLINE_PREFIX + variantId);
      }
    } catch (e) {
      // Storage unavailable (private browsing, etc) - not fatal, we just
      // won't remember a decline across reconcile passes.
    }
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.ok ? res.json() : null;
    });
  }

  function groupByVariant(cart) {
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var isGift = !!(item.properties && item.properties[GIFT_PROPERTY] === 'true');
      var vid = item.variant_id;
      if (!groups[vid]) {
        groups[vid] = { paidQty: 0, giftLine: null };
      }
      if (isGift) {
        groups[vid].giftLine = item;
      } else if (item.properties && Object.prototype.hasOwnProperty.call(item.properties, GIFT_PROPERTY)) {
        // Only variants that go through the SmileO bundle flow carry this
        // property at all - that's how we scope this script to bundle
        // products instead of every product in the store.
        groups[vid].paidQty += item.quantity;
      }
    });
    return groups;
  }

  function addGiftUnit(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: { _bundle_free: 'true', _bundle_gift: 'true' },
      }),
    });
  }

  function removeGiftLine(lineKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
  }

  function reconcile() {
    if (busy) return;
    fetchCart()
      .then(function (cart) {
        if (!cart) return;
        var groups = groupByVariant(cart);
        var addVariant = null;
        var removeKey = null;

        Object.keys(groups).forEach(function (vid) {
          var g = groups[vid];

          if (g.paidQty < THRESHOLD) {
            // Left the "4 club" - clear any decline flag so a future run
            // back up to 4 is treated as a fresh gift offer, and make sure
            // a leftover gift line (including the case paidQty dropped all
            // the way to 0) doesn't stick around.
            setDeclined(vid, false);
            if (g.giftLine) removeKey = g.giftLine.key;
            return;
          }

          if (g.paidQty >= THRESHOLD && !g.giftLine && !isDeclined(vid)) {
            addVariant = vid;
          }
        });

        if (addVariant) {
          busy = true;
          return addGiftUnit(addVariant).then(function () {
            window.location.reload();
          });
        }

        if (removeKey) {
          busy = true;
          return removeGiftLine(removeKey).then(function () {
            window.location.reload();
          });
        }
      })
      .catch(function () {
        busy = false;
      });
  }

  // Intentionally NOT a single shared/cancel-and-replace timer: several of
  // these can be in flight at once (e.g. a 1000ms check and a 2500ms safety
  // net from the same click). reconcile()'s own `busy` guard makes that
  // safe - a redundant call just re-checks the cart and finds nothing to do.
  function scheduleReconcile(delay) {
    setTimeout(reconcile, delay);
  }

  // When the customer clicks something that removes/decrements the gift
  // row itself, remember it as declined BEFORE reconcile() runs again, so
  // reconcile doesn't immediately add it back.
  function rememberIfGiftDecline(target) {
    var row = target.closest('tr[data-line-key]');
    if (!row || !row.querySelector('.smio-cart-gift-tag')) return;

    var lineKey = row.getAttribute('data-line-key');
    fetchCart().then(function (cart) {
      if (!cart) return;
      var line = (cart.items || []).filter(function (it) {
        return it.key === lineKey;
      })[0];
      if (line) setDeclined(line.variant_id, true);
    });
  }

  function init() {
    reconcile();

    // Best case: Dawn's pub/sub cart-update event, when it's exposed as a
    // plain global (older/classic-script Dawn builds). Newer Dawn ships
    // pubsub as an ES module, so `subscribe`/`PUB_SUB_EVENTS` may simply not
    // exist on window - that's fine, the listeners below don't depend on it.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        scheduleReconcile(400);
      });
    }

    // Robust fallback that doesn't depend on any theme-internal API: watch
    // for the DOM interactions that change cart quantity (+/- buttons,
    // typing a new quantity, removing a line) and re-check the cart a beat
    // later, once the theme's own AJAX request has had time to finish.
    document.addEventListener('click', function (e) {
      var target = e.target.closest
        ? e.target.closest('.quantity__button, cart-remove-button, .cart-remove-button')
        : null;
      if (!target) return;

      var isRemove = target.tagName === 'CART-REMOVE-BUTTON' || target.classList.contains('cart-remove-button');
      var isMinus = target.classList.contains('quantity__button') && target.getAttribute('name') === 'minus';

      if (isRemove || isMinus) {
        rememberIfGiftDecline(target);
      }

      scheduleReconcile(1000);
      scheduleReconcile(2500); // safety net in case the first check ran before the server-side update landed
    });

    document.addEventListener('change', function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('quantity__input')) {
        scheduleReconcile(1000);
        scheduleReconcile(2500);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
